import type {
  ConciseBranch,
  ConciseWorkspace,
} from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  STANDARD_BRANCH,
  type WorkspaceKey,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type ConfigurableServer,
  ConfigurableServers,
  getObjectEntries,
  type NonEmptyArray,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import type { TreeNodeData } from '@festina-life/ui/listsAndTables'

import { ServerServices } from '../../../uiModels/openApiServices'
import type {
  BranchLeaf,
  BranchTreeElementKey,
  CommonBranchNodeInputParameters,
  ConciseBranchTreeNode,
} from '../types'

export const SEPARATOR_PATTERN = /[/~]/

const extractPathSegments = (branchName: string): string[] => {
  const parts = branchName.split(SEPARATOR_PATTERN)
  const separators: string[] = []

  // Extract which separator was used at each position
  for (const charString of branchName) {
    if (SEPARATOR_PATTERN.test(charString)) {
      separators.push(charString)
    }
  }

  // Rebuild paths preserving original separators
  const paths: string[] = [parts[0]]
  for (let i = 1; i < parts.length; i++) {
    paths.push(`${paths[i - 1]}${separators[i - 1]}${parts[i]}`)
  }

  return paths
}

export const createFolderHierarcy = (conciseBranches: ConciseBranch[]) => {
  const allUniquePaths = Array.from(
    // We have to use Array.from since Set.prototype.toArray() is not available in Nodejs for testing
    new Set(
      conciseBranches
        .flatMap((conciseBranch) =>
          // Converts foo/bar/xyz to ['foo', 'foo/bar', 'foo/bar/xyz']
          // Converts foo~bar~xyz to ['foo', 'foo~bar', 'foo~bar~xyz']
          // Converts foo/bar~xyz to ['foo', 'foo/bar', 'foo/bar~xyz']
          conciseBranch.name ? extractPathSegments(conciseBranch.name) : [],
        )
        .filter(notEmpty),
    ),
  )

  return allUniquePaths.reduce<Record<string, string[]>>(
    (acc, uniquePath) => ({
      ...acc,
      [uniquePath]: allUniquePaths.filter((path) => {
        if (!path.startsWith(uniquePath)) {
          return false
        }

        const charAfterPrefix = path[uniquePath.length]
        if (!charAfterPrefix || !SEPARATOR_PATTERN.test(charAfterPrefix)) {
          return false
        }

        const remainder = path.slice(uniquePath.length + 1)
        return remainder.length > 0 && !SEPARATOR_PATTERN.test(remainder)
      }),
    }),
    {},
  )
}

export const createBaseBranchHierarchy = (conciseBranches: ConciseBranch[]) => {
  const branchNames = conciseBranches
    .map((branch) => branch.name)
    .filter(notEmpty)

  return branchNames.reduce<Record<string, string[]>>(
    (acc, branchName) => ({
      ...acc,
      [branchName]: conciseBranches
        .filter((conciseBranch) => conciseBranch.baseBranchName === branchName)
        .map((conciseBranch) => conciseBranch.name)
        .filter(notEmpty),
    }),
    {},
  )
}

const createConciseBranchTreeNode = ({
  serverWorkspaces,
  ...other
}: {
  serverWorkspaces?: Optional<ConciseWorkspace[]>
} & (
  | { isFolder: true; name: string }
  | { isFolder: false; conciseBranch: ConciseBranch }
)): ConciseBranchTreeNode => {
  const commonValues = {
    children: [],
    isRoot: true,
    parentName: undefined,
  }

  return other.isFolder
    ? { ...commonValues, isFolder: true, name: other.name }
    : {
        ...commonValues,
        isFolder: false,
        branch: other.conciseBranch,
        workspaces:
          serverWorkspaces?.filter(
            (workspace) =>
              workspace.baseBranchName === other.conciseBranch.name,
          ) ?? [],
      }
}

export const createBranchHierarchy = ({
  serverBranches,
  serverWorkspaces,
  createHierarcy = createBaseBranchHierarchy,
}: {
  serverBranches: ConciseBranch[]
  serverWorkspaces?: Optional<ConciseWorkspace[]>
  createHierarcy?: (
    conciseBranches: ConciseBranch[],
  ) => Record<string, string[]>
}): Map<string, ConciseBranchTreeNode> => {
  const branchHierarchy = createHierarcy(serverBranches)
  const hierarchyMap = new Map<string, ConciseBranchTreeNode>()
  const serverBranchNames = serverBranches.map((branch) => branch.name)

  getObjectEntries(branchHierarchy).forEach(([nodeKey, children]) => {
    let currentTreeNode = hierarchyMap.get(nodeKey)

    if (!currentTreeNode) {
      const isFolder = !serverBranchNames.includes(nodeKey)
      currentTreeNode = isFolder
        ? createConciseBranchTreeNode({ isFolder: true, name: nodeKey })
        : createConciseBranchTreeNode({
            isFolder: false,
            conciseBranch: serverBranches.find(
              (branch) => branch.name === nodeKey,
            )!,
            serverWorkspaces,
          })

      hierarchyMap.set(nodeKey, currentTreeNode)
    }

    children.forEach((child) => {
      let childTreeNode = hierarchyMap.get(child)
      if (!childTreeNode) {
        const isFolder = !serverBranchNames.includes(child)
        childTreeNode = isFolder
          ? createConciseBranchTreeNode({ isFolder: true, name: child })
          : createConciseBranchTreeNode({
              isFolder: false,
              conciseBranch: serverBranches.find(
                (branch) => branch.name === child,
              )!,
              serverWorkspaces,
            })

        hierarchyMap.set(child, childTreeNode)
      }
      childTreeNode.isRoot = false
      childTreeNode.parentName = nodeKey

      if (!currentTreeNode.children.includes(childTreeNode)) {
        currentTreeNode.children.push(childTreeNode)
      }
    })
  })

  return hierarchyMap
}

export const getChildHierarchy = ({
  conciseBranches,
  forNode,
  hierarchy,
}: {
  conciseBranches: ConciseBranch[]
  forNode: string
  hierarchy: string[]
}) => {
  if (hierarchy.includes(forNode)) {
    return
  }

  const branchHierarchy = createBaseBranchHierarchy(conciseBranches)
  hierarchy.push(forNode)

  branchHierarchy[forNode].forEach((child) =>
    getChildHierarchy({
      conciseBranches,
      forNode: child,
      hierarchy,
    }),
  )
}

const sortBranchNodes = (
  a: ConciseBranchTreeNode,
  b: ConciseBranchTreeNode,
) => {
  const aName = a.isFolder ? a.name : a.branch.name
  const bName = b.isFolder ? b.name : b.branch.name
  assertNotEmpty(aName)
  assertNotEmpty(bName)

  // Folders first: folders sort before non-folders
  const isAFolderOrNonLeaf = a.isFolder || !!a.children.length
  const isBFolderOrNonLeaf = b.isFolder || !!b.children.length

  if (isAFolderOrNonLeaf && !isBFolderOrNonLeaf) {
    return -1
  }
  if (!isAFolderOrNonLeaf && isBFolderOrNonLeaf) {
    return 1
  }

  return aName.localeCompare(bName)
}

export const generateTreeNodesForServer = ({
  server,
  conciseBranchNodes,
  activeElementKey,
  branchCategoryTranslations,
  query,
  standardBranchTranslation,
}: CommonBranchNodeInputParameters & {
  conciseBranchNodes: ConciseBranchTreeNode[]
  query: Optional<string>
  standardBranchTranslation: string
}): TreeNodeData<BranchLeaf> => {
  const path: NonEmptyArray<string> = [server]
  const isStandardComponentConfigured =
    !!ServerServices[server].api.OpenAPI.BASE

  const standardComponentLeaf: Optional<BranchLeaf> =
    isStandardComponentConfigured &&
    (!query || standardBranchTranslation.toLowerCase().includes(query))
      ? {
          id: standardBranchTranslation,
          isStandardBranch: true,
          packageName: undefined,
          isActive:
            isBranchTreeElementBranchKey(activeElementKey) &&
            activeElementKey?.name === STANDARD_BRANCH &&
            activeElementKey.server === server,
          tags: [],
          path: [server, STANDARD_BRANCH],
        }
      : undefined

  const branchLeaves = conciseBranchNodes.filter(
    (branchNode) =>
      !branchNode.children.length &&
      !branchNode.isFolder &&
      branchNode.workspaces.length <= 1,
  )
  const branchNodes = conciseBranchNodes
    .filter((specification) => !branchLeaves.includes(specification))
    .toSorted(sortBranchNodes)

  return {
    name: server,
    isActive:
      isBranchTreeElementConfigurableServer(activeElementKey) &&
      activeElementKey === server,
    tags: [],
    leaves: branchLeaves
      .map((spefication) =>
        toBranchLeaf({
          server,
          conciseBranchNode: spefication,
          activeElementKey,
          path,
        }),
      )
      .concat(standardComponentLeaf ? [standardComponentLeaf] : []),
    path,
    nodes: branchNodes.map((specificationNode) =>
      toBranchNode({
        server,
        conciseBranchNode: specificationNode,
        currentPath: path,
        activeElementKey,
        branchCategoryTranslations,
      }),
    ),
  }
}

const toBranchLeaf = ({
  server,
  conciseBranchNode,
  activeElementKey,
  path,
}: {
  server: ConfigurableServer
  conciseBranchNode: ConciseBranchTreeNode
  activeElementKey: Optional<BranchTreeElementKey>
  path: string[]
}): BranchLeaf => {
  if (conciseBranchNode.isFolder) {
    throw new Error('Foldres should not be parsed as leaves')
  }
  assertNotEmpty(conciseBranchNode.branch.name)

  const isUnifiedWorkspaceBranch = conciseBranchNode.workspaces.length === 1
  const isBranchKeyActive =
    isBranchTreeElementBranchKey(activeElementKey) &&
    conciseBranchNode.branch.name === activeElementKey.name &&
    server === activeElementKey.server
  const isWorkspaceKeyActive =
    isUnifiedWorkspaceBranch &&
    isBranchTreeElementWorkspaceKey(activeElementKey) &&
    activeElementKey.branchKey.name === conciseBranchNode.branch.name &&
    activeElementKey.branchKey.server === server

  return {
    id: conciseBranchNode.branch.name,
    isStandardBranch: false,
    isWorkspace: false,
    isUnifiedWorkspaceBranch,
    tags: [],
    data: conciseBranchNode.branch,
    path: [...path, conciseBranchNode.branch.name],
    isActive: isBranchKeyActive || isWorkspaceKeyActive,
  }
}

const toBranchWorkspaceLeaf = ({
  server,
  conciseWorkspace,
  activeElementKey,
  path,
}: {
  server: ConfigurableServer
  conciseWorkspace: ConciseWorkspace
  activeElementKey: Optional<BranchTreeElementKey>
  path: string[]
}): BranchLeaf => {
  assertNotEmpty(conciseWorkspace.name)

  return {
    id: conciseWorkspace.name,
    isStandardBranch: false,
    isWorkspace: true,
    tags: [],
    data: conciseWorkspace,
    path: [...path, conciseWorkspace.name],
    isActive:
      !!isBranchTreeElementWorkspaceKey(activeElementKey) &&
      conciseWorkspace.name === activeElementKey.name &&
      server === activeElementKey.branchKey.server,
  }
}

const toBranchNode = ({
  conciseBranchNode,
  ...commonProps
}: CommonBranchNodeInputParameters & {
  currentPath: string[]
  conciseBranchNode: ConciseBranchTreeNode
}): TreeNodeData<BranchLeaf> => {
  const { server, activeElementKey, currentPath } = commonProps
  const nodeName = conciseBranchNode.isFolder
    ? conciseBranchNode.name
    : conciseBranchNode.branch.name

  assertNotEmpty(nodeName)
  const path = currentPath.concat(nodeName) as NonEmptyArray<string>

  const leafConciseBranches = conciseBranchNode.children.filter(
    (childSpecification) =>
      !childSpecification.children.length &&
      (childSpecification.isFolder
        ? false
        : childSpecification.workspaces.length <= 1),
  )
  const leafConciseWorkspaces = conciseBranchNode.isFolder
    ? []
    : conciseBranchNode.workspaces

  const branchLeaves = leafConciseBranches.map((conciseBranchNode) =>
    toBranchLeaf({
      server,
      conciseBranchNode,
      activeElementKey,
      path,
    }),
  )

  const workspaceLeaves =
    leafConciseWorkspaces.length > 1
      ? leafConciseWorkspaces.map((conciseWorkspace) =>
          toBranchWorkspaceLeaf({
            conciseWorkspace,
            server,
            activeElementKey,
            path,
          }),
        )
      : []

  const nodes = conciseBranchNode.children
    .filter((childNode) => !leafConciseBranches.includes(childNode))
    .toSorted(sortBranchNodes)

  return {
    name: nodeName,
    tags:
      !conciseBranchNode.isFolder && conciseBranchNode.branch.branchCategory
        ? [conciseBranchNode.branch.branchCategory]
        : [],
    leaves: [...branchLeaves, ...workspaceLeaves],
    path,
    isActive:
      isBranchTreeElementBranchKey(activeElementKey) &&
      !conciseBranchNode.isFolder &&
      conciseBranchNode.branch.name === activeElementKey.name &&
      server === activeElementKey.server,
    nodes: nodes.map((childNode) =>
      toBranchNode({
        conciseBranchNode: childNode,
        ...commonProps,
      }),
    ),
  }
}

export const getBranchServerFromPath = (path: string[]): ConfigurableServer =>
  path.at(0) as ConfigurableServer

export const isBranchTreeElementWorkspaceKey = (
  key: Optional<BranchTreeElementKey>,
): key is WorkspaceKey => !!key && typeof key === 'object' && 'branchKey' in key

export const isBranchTreeElementConfigurableServer = (
  key: Optional<BranchTreeElementKey>,
): key is ConfigurableServer =>
  !!key && typeof key === 'string' && ConfigurableServers.includes(key)

export const isBranchTreeElementBranchKey = (
  key: Optional<BranchTreeElementKey>,
): key is BranchKey =>
  !!key &&
  !isBranchTreeElementConfigurableServer(key) &&
  !isBranchTreeElementWorkspaceKey(key)
